import {
	Component,
	EventEmitter,
	Optional,
	Output,
	ViewChild,
} from '@angular/core';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import { AppTagConfiguratorComponent } from '@datachain/ui-sdk/components/app-tag-configurator';
import { AppTagItemEntity } from '@datachain/ui-sdk/components/dc-types';
import { OrderedSet } from 'immutable';
import {
	concat,
	debounceTime,
	filter,
	merge,
	Observable,
	of,
	switchMap,
	takeUntil,
	tap,
	withLatestFrom,
} from 'rxjs';

import { configuration } from '../../../core/configuration';
import { EndpointRequester } from '../../../requesters/async-validators/endpoint.requester';
import { ValidationErrorKeys } from '../../../ui/form/validation-erros-keys';
import {
	ExpositionDiagnosticError,
	ExpositionErrorKey,
} from '../../domain/exposition-diagnostic-errors.entity';
import { ExpositionProvider } from '../../providers/exposition.provider';
import { ExpositionEndpointMetadataEntity } from './exposition-endpoint-metadata.entity';
import {
	EndpointMetadataFormControls,
	ExpositionEndpointMetadataForm,
} from './exposition-endpoint-metadata.form';

@Component({
	selector: 'app-exposition-endpoint-metadata-config',
	templateUrl: './exposition-endpoint-metadata-config.component.html',
	styleUrls: ['./exposition-endpoint-metadata-config.component.scss'],
	inputs: ['expositionMetadata', 'isInViewMode', 'expositionId'],
	providers: [ExpositionEndpointMetadataForm, EndpointRequester],
})
export class ExpositionEndpointMetadataConfigComponent extends DcBaseComponent {
	public Appearance: MatFormFieldAppearance = 'legacy';

	@ViewChild(AppTagConfiguratorComponent)
	public tagConfiguratorComponentCmp: AppTagConfiguratorComponent | null = null;

	@Output()
	public hasEndPointMetadataChanged = new EventEmitter<boolean>();

	public vo$: Observable<{
		expositionMetadata: ExpositionEndpointMetadataEntity;
		isInViewMode: boolean;
	}>;

	protected readonly EndpointMetadataFormControls =
		EndpointMetadataFormControls;
	protected readonly ValidationErrorKeys = ValidationErrorKeys;
	protected readonly Configuration = configuration;

	public constructor(
		public readonly endpointMetadataForm: ExpositionEndpointMetadataForm,
		private readonly endpointRequester: EndpointRequester,
		@Optional()
		private readonly expositionProvider: ExpositionProvider
	) {
		super();
		this.cmpId = 'exposition-endpoint-metadata-config';

		const expositionId$ = merge(
			of(null),
			this.toObservable<number>('expositionId')
		);

		const inViewMode$ = merge(
			of(false),
			this.toObservable<boolean>('isInViewMode')
		);

		this.toObservable<ExpositionEndpointMetadataEntity>('expositionMetadata')
			.pipe(
				takeUntil(this.onDestroy$),
				tap((endpointMetadata) => {
					this.endpointMetadataForm.populate(endpointMetadata);
				})
			)
			.subscribe();

		concat(
			of(this.endpointMetadataForm.form.value),
			this.endpointMetadataForm.form.valueChanges.pipe(debounceTime(1000))
		)
			.pipe(
				takeUntil(this.onDestroy$),
				withLatestFrom(this.toObservable<boolean>('isInViewMode')),
				filter(([_, isInViewMode]) => !isInViewMode),
				tap(() => {
					if (!this.endpointMetadataForm.form.valid) {
						const hasAccessPointRequired = endpointMetadataForm.form
							.get(EndpointMetadataFormControls.AccessPoint)
							?.hasError(ValidationErrorKeys.Required);
						const hasTitleRequired = endpointMetadataForm.form
							.get(EndpointMetadataFormControls.Title)
							?.hasError(ValidationErrorKeys.Required);

						if (hasAccessPointRequired || hasTitleRequired) {
							this.expositionProvider.addError(
								ExpositionDiagnosticError.build({
									key: ExpositionErrorKey.EndpointMetadataMissingFields,
									type: 'critic',
									message: $localize`:@@exposition.diagnosticError.endpointMetadataMissingFields:`,
								})
							);
							this.expositionProvider.removeError(
								ExpositionErrorKey.EndpointMetadataNotValid
							);
						} else {
							this.expositionProvider.addError(
								ExpositionDiagnosticError.build({
									key: ExpositionErrorKey.EndpointMetadataNotValid,
									type: 'critic',
									message: $localize`:@@exposition.diagnosticError.endpointMetadataNotValid:`,
								})
							);
							this.expositionProvider.removeError(
								ExpositionErrorKey.EndpointMetadataMissingFields
							);
						}
					} else {
						this.expositionProvider.removeError(
							ExpositionErrorKey.EndpointMetadataMissingFields
						);
						this.expositionProvider.removeError(
							ExpositionErrorKey.EndpointMetadataNotValid
						);
					}
					this.hasEndPointMetadataChanged.emit();
				})
			)
			.subscribe();

		const accessPointCtrl = this.endpointMetadataForm.form.get(
			EndpointMetadataFormControls.AccessPoint
		);
		if (accessPointCtrl) {
			combineAll({
				ctrlVal: accessPointCtrl.valueChanges,
				expositionId: expositionId$,
				inViewMode: inViewMode$,
			})
				.pipe(
					takeUntil(this.onDestroy$),
					debounceTime(320),
					switchMap(({ ctrlVal, expositionId, inViewMode }) => {
						if (accessPointCtrl.valid && !inViewMode) {
							return this.endpointRequester.validateEndpointName(
								ctrlVal,
								expositionId
							);
						}
						return of(null);
					}),
					tap((res) => {
						if (res === null) {
							return;
						}
						if (!res) {
							return accessPointCtrl.setErrors({
								[ValidationErrorKeys.UnavailableToUse]: true,
							});
						} else {
							return accessPointCtrl.setErrors(null);
						}
					})
				)
				.subscribe();
		}

		// this.endpointMetadataForm.form.statusChanges
		// 	.pipe(
		// 		takeUntil(this.onDestroy$),
		// 		tap((st) => console.log(st))
		// 	)
		// 	.subscribe();

		this.vo$ = combineAll({
			expositionMetadata: merge(
				of(ExpositionEndpointMetadataEntity.build({})),
				this.toObservable<ExpositionEndpointMetadataEntity>(
					'expositionMetadata'
				)
			),
			isInViewMode: inViewMode$,
		});
	}

	public hasKeyWordsChanged(isChanged: boolean): void {
		this.hasEndPointMetadataChanged.emit(isChanged);
	}

	public async extractFormData(): Promise<ExpositionEndpointMetadataEntity> {
		let keywords = new Array<AppTagItemEntity>();
		if (this.tagConfiguratorComponentCmp) {
			keywords = await this.tagConfiguratorComponentCmp.getUsedTags();
		}
		const endpointMetadata = this.endpointMetadataForm.extract();
		return ExpositionEndpointMetadataEntity.build({
			accessPoint: endpointMetadata?.accessPoint,
			title: endpointMetadata?.title,
			details: endpointMetadata?.details,
			keywords: OrderedSet(keywords),
			synthesisType: endpointMetadata.synthesisType,
		});
	}
}
